"use client";

import { useState, useMemo, useCallback } from "react";
import { Search, ChevronRight, ChevronLeft, Folder, Check } from "lucide-react";
import { SymbolIcon } from "@/components/common";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { Badge } from "@/shadcn-components/ui/badge";
import { useSymbolsStore } from "@/vybe-trader-client/symbols/stores/useSymbolsStore";
import { useQuotesWatchlistStore } from "@/lib/quotes-watchlist";
import { collectSymbolsInFolder } from "./folderSymbols";
import type { Symbol } from "@/vybe-trader-client/symbols/types";

interface SymbolSelectionScreenProps {
  onBack: () => void;
  isDesktop?: boolean;
}

interface NavigationLevel {
  type: "root" | "group" | "symbols";
  path: string[];
}

interface GroupNode {
  name: string;
  path: string[];
  children: Map<string, GroupNode>;
  symbols: Symbol[];
  totalSymbolsCount: number;
  subscribedCount: number;
}

export default function SymbolSelectionScreen({
  onBack,
  isDesktop = false,
}: SymbolSelectionScreenProps) {
  const loadingStatus = useSymbolsStore((state) => state.loadingStatus);
  const getCatalogsByGroup = useSymbolsStore((state) => state.getCatalogsByGroup);
  const getSymbolsByGroupPath = useSymbolsStore((state) => state.getSymbolsByGroupPath);
  const getSymbolsByQuery = useSymbolsStore((state) => state.getSymbolsByQuery);
  const watchlist = useQuotesWatchlistStore((state) => state.watchlist);

  const [searchQuery, setSearchQuery] = useState("");
  const [navigationLevel, setNavigationLevel] = useState<NavigationLevel>({
    type: "root",
    path: [],
  });

  // Create a memoized function to check subscription status
  const getSubscriptionStatus = useCallback(
    (currency: string) => {
      return watchlist.some(item => item.symbolKey === currency);
    },
    [watchlist]
  );

  // Function to get all symbols in a folder and its subfolders
  const getAllSymbolsInFolder = useCallback((folderPath: string[]) => {
    return collectSymbolsInFolder(
      useSymbolsStore.getState().byCurrency.values(),
      folderPath
    );
  }, []);

  // Get filtered symbols for search
  const filteredSymbols = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return getSymbolsByQuery(searchQuery);
  }, [searchQuery, getSymbolsByQuery]);

  // Get current items based on navigation
  const currentItems = useMemo(() => {
    // If searching, show filtered symbols
    if (searchQuery.trim()) {
      return filteredSymbols.map((symbol) => ({
        type: "symbol" as const,
        symbol,
      }));
    }

    // Get catalogs for current path
    const catalogs = getCatalogsByGroup(navigationLevel.path);
    
    // If we have subfolders, show them
    if (catalogs.length > 0) {
      return catalogs.map((catalog) => {
        // Calculate subscribed count for this folder (including subfolders)
        const allSymbolsInFolder = getAllSymbolsInFolder(catalog.path);
        const subscribedCount = allSymbolsInFolder.filter(symbol => 
          getSubscriptionStatus(symbol.currency)
        ).length;

        return {
          type: "folder" as const,
          node: {
            name: catalog.name,
            path: catalog.path,
            children: new Map(), // Not needed for display
            symbols: [],
            totalSymbolsCount: catalog.symbolsCount,
            subscribedCount,
          } as GroupNode,
        };
      });
    }
    
    // Otherwise get symbols for current path
    const symbols = getSymbolsByGroupPath(navigationLevel.path);
    return symbols.map((symbol) => ({
      type: "symbol" as const,
      symbol,
    }));
  }, [navigationLevel.path, searchQuery, filteredSymbols, getCatalogsByGroup, getSymbolsByGroupPath, getSubscriptionStatus, getAllSymbolsInFolder]);

  // Navigation handlers
  const navigateTo = useCallback((path: string[]) => {
    setSearchQuery("");
    setNavigationLevel({
      type: path.length === 0 ? "root" : "group",
      path,
    });
  }, []);

  const goBack = useCallback(() => {
    if (searchQuery) {
      setSearchQuery("");
    } else if (navigationLevel.path.length > 0) {
      const newPath = navigationLevel.path.slice(0, -1);
      navigateTo(newPath);
    } else {
      onBack();
    }
  }, [searchQuery, navigationLevel.path, navigateTo, onBack]);

  // Get current breadcrumb
  const breadcrumb = useMemo(() => {
    if (searchQuery) {
      return `Search Results (${filteredSymbols.length})`;
    }
    if (navigationLevel.path.length === 0) {
      return "Select Symbols";
    }
    return navigationLevel.path.join(" / ");
  }, [searchQuery, filteredSymbols.length, navigationLevel.path]);

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      {/* Header according to developer-guidelines */}
      <div className={`flex shrink-0 items-center justify-between ${isDesktop ? 'h-8 px-2' : 'h-11 px-3'} bg-background border-b border-border`}>
        <Button
          variant="ghost"
          size="icon"
          onClick={goBack}
          className={`${isDesktop ? 'h-6 w-6' : 'h-9 w-9'} hover:bg-transparent`}
        >
          <ChevronLeft className={`${isDesktop ? 'h-3 w-3' : 'h-4 w-4'} text-muted-foreground`} />
        </Button>
        <h1 className={`${isDesktop ? 'text-xs' : 'text-sm'} font-medium text-foreground truncate flex-1 text-center mx-4`}>
          {breadcrumb}
        </h1>
        <Button
          variant="ghost"
          onClick={onBack}
          className={`text-primary hover:bg-transparent ${isDesktop ? 'text-xs h-6 px-2' : 'text-sm h-9 px-2'}`}
        >
          Cancel
        </Button>
      </div>

      {/* Search Bar */}
      <div className={`shrink-0 ${isDesktop ? 'px-2 py-1.5' : 'px-3 py-1.5'} border-b border-border`}>
        <div className="relative">
          <Search className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 ${isDesktop ? 'h-3 w-3' : 'h-4 w-4'} text-muted-foreground`} />
          <Input
            type="text"
            placeholder="Search symbols..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`border-0 bg-accent ${isDesktop ? 'pl-8 text-xs h-7' : 'pl-8 text-xs h-9'} placeholder:text-muted-foreground`}
          />
        </div>
      </div>

      {/* Content List */}
      <div className="flex-1 overflow-y-auto">
        {loadingStatus === "loading" ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-muted-foreground">Loading symbols...</div>
          </div>
        ) : loadingStatus === "error" ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-red-400">Error loading symbols</div>
          </div>
        ) : currentItems.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-muted-foreground text-center px-4">
              {searchQuery ? (
                <>
                  <p className="text-lg mb-2">No symbols found</p>
                  <p className="text-sm">
                    Try searching with different keywords
                  </p>
                </>
              ) : (
                "No items available"
              )}
            </div>
          </div>
        ) : (
          <>
            {currentItems.map((item, index) => {
              if (item.type === "folder") {
                return (
                  <FolderItem
                    key={item.node.path.join("/")}
                    node={item.node}
                    onNavigate={() => navigateTo(item.node.path)}
                    isLast={index === currentItems.length - 1}
                    isDesktop={isDesktop}
                  />
                );
              } else {
                return (
                  <SymbolItem
                    key={item.symbol.currency}
                    symbol={item.symbol}
                    isLast={index === currentItems.length - 1}
                    isDesktop={isDesktop}
                  />
                );
              }
            })}
          </>
        )}
      </div>
    </div>
  );
}

// Folder item component
interface FolderItemProps {
  node: GroupNode;
  onNavigate: () => void;
  isLast: boolean;
  isDesktop?: boolean;
}

const FolderItem: React.FC<FolderItemProps> = ({
  node,
  onNavigate,
  isLast,
  isDesktop = false,
}) => {
  return (
    <Button
      variant="ghost"
      onClick={onNavigate}
      className={`w-full h-auto ${isDesktop ? 'p-2' : 'px-3 py-2.5'} flex items-center justify-between hover:bg-accent rounded-none ${
        !isLast ? "border-b border-border" : ""
      }`}
    >
      <div className="flex items-center gap-3">
        <Folder className={`${isDesktop ? 'h-4 w-4' : 'h-4 w-4'} text-orange-500`} />
        <span className={`text-foreground ${isDesktop ? 'text-sm' : 'text-sm'} font-medium`}>{node.name}</span>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className={`font-mono ${isDesktop ? 'text-[10px]' : 'text-xs'}`}>
          {node.subscribedCount} / {node.totalSymbolsCount}
        </Badge>
        <ChevronRight className={`${isDesktop ? 'h-3 w-3' : 'h-4 w-4'} text-muted-foreground`} />
      </div>
    </Button>
  );
};

// Symbol item component with subscription logic
interface SymbolItemProps {
  symbol: Symbol;
  isLast: boolean;
  isDesktop?: boolean;
}

const SymbolItem: React.FC<SymbolItemProps> = ({
  symbol,
  isLast,
  isDesktop = false,
}) => {
  const watchlist = useQuotesWatchlistStore((state) => state.watchlist);
  const addToWatchlist = useQuotesWatchlistStore((state) => state.addToWatchlist);
  const removeFromWatchlist = useQuotesWatchlistStore((state) => state.removeFromWatchlist);
  
  const isSubscribed = watchlist.some(item => item.symbolKey === symbol.currency);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleToggle = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    try {
      if (isSubscribed) {
        await removeFromWatchlist(symbol);
      } else {
        await addToWatchlist(symbol);
      }
    } catch (error) {
      console.error("Failed to toggle subscription:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Button
      variant="ghost"
      onClick={handleToggle}
      disabled={isProcessing}
      className={`w-full h-auto ${isDesktop ? 'p-2' : 'px-3 py-2.5'} flex items-center justify-between hover:bg-accent rounded-none ${
        !isLast ? "border-b border-border" : ""
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <SymbolIcon
            name={symbol.currency}
            group={symbol.groupName}
            description={symbol.description}
            size={16}
          />
          <span className={`text-foreground ${isDesktop ? 'text-sm' : 'text-sm'} font-medium`}>
            {symbol.currency}
          </span>
          <span className={`text-muted-foreground ${isDesktop ? 'text-xs' : 'text-xs'} truncate text-left`}>
            {symbol.description}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {isSubscribed && (
          <Check className={`${isDesktop ? 'h-3 w-3' : 'h-4 w-4'} text-green-500`} />
        )}
        <Badge 
          variant={isSubscribed ? "default" : "secondary"} 
          className={`${isDesktop ? 'text-[10px]' : 'text-xs'} ${
            isSubscribed ? "bg-green-600 hover:bg-green-700" : ""
          }`}
        >
          {isSubscribed ? "Added" : "Add"}
        </Badge>
      </div>
    </Button>
  );
};